import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Instagram, X } from 'lucide-react';
import api from '../utils/axios';

function toEmbedUrl(reelUrl) {
  if (!reelUrl) return '';
  const cleaned = String(reelUrl).trim().split('?')[0].replace(/\/+$/, '');
  if (/\/embed\/?$/i.test(cleaned)) return `${cleaned}/`;
  // Prefer captioned embed so likes / comments / share UI can appear
  if (/\/reel\//i.test(cleaned) || /\/p\//i.test(cleaned) || /\/tv\//i.test(cleaned)) {
    return `${cleaned}/embed/captioned/`;
  }
  return `${cleaned}/embed/captioned/`;
}

function ReelEmbed({ reel, active, preload }) {
  const shouldMount = active || preload;
  const embedUrl = useMemo(() => toEmbedUrl(reel?.reel_url), [reel?.reel_url]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (shouldMount && !mounted) {
      setMounted(true);
    } else if (!shouldMount && mounted) {
      setMounted(false);
    }
  }, [shouldMount, mounted]);

  if (!shouldMount || !embedUrl) {
    return <div className="absolute inset-0 bg-black" />;
  }

  return (
    <div className="absolute inset-0 bg-black overflow-hidden">
      {mounted && (
        <iframe
          key={`${reel.id}-${active ? 'active' : 'preload'}`}
          title={`Instagram reel ${reel.id}`}
          src={embedUrl}
          className="absolute inset-0 w-full h-full border-0"
          loading={active ? 'eager' : 'lazy'}
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      )}
    </div>
  );
}

export default function InstagramReelsViewer({ open, onClose, reels }) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [hasSwiped, setHasSwiped] = useState(false);
  const touchStartY = useRef(null);
  const touchStartX = useRef(null);
  const wheelLock = useRef(false);
  const touchLock = useRef(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setIndex(0);
      setDirection(0);
      setHasSwiped(false);
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = '0';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [open]);

  const goNext = useCallback(() => {
    setIndex((prev) => {
      if (prev >= reels.length - 1) return prev;
      setDirection(1);
      setHasSwiped(true);
      return prev + 1;
    });
  }, [reels.length]);

  const goPrev = useCallback(() => {
    setIndex((prev) => {
      if (prev <= 0) return prev;
      setDirection(-1);
      setHasSwiped(true);
      return prev - 1;
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        setHasSwiped(true);
        goNext();
      }
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        setHasSwiped(true);
        goPrev();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, goNext, goPrev]);

  // Track when user navigates to hide swipe indication
  useEffect(() => {
    if (index > 0) {
      setHasSwiped(true);
    }
  }, [index]);

  const onWheel = (event) => {
    if (wheelLock.current) return;
    if (Math.abs(event.deltaY) < 28) return;
    wheelLock.current = true;
    if (event.deltaY > 0) goNext();
    else goPrev();
    window.setTimeout(() => {
      wheelLock.current = false;
    }, 420);
  };

  const onTouchStart = (event) => {
    if (touchLock.current) return;
    touchStartY.current = event.touches[0]?.clientY ?? null;
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };

  const onTouchMove = (event) => {
    if (touchStartY.current === null || touchStartX.current === null) return;
    
    const currentY = event.touches[0]?.clientY ?? touchStartY.current;
    const currentX = event.touches[0]?.clientX ?? touchStartX.current;
    
    const deltaY = Math.abs(currentY - touchStartY.current);
    const deltaX = Math.abs(currentX - touchStartX.current);
    
    // If horizontal movement is greater than vertical, allow horizontal scroll
    if (deltaX > deltaY && deltaX > 10) {
      touchStartY.current = null;
      touchStartX.current = null;
      return;
    }
    
    // Prevent vertical scroll if it's a vertical swipe
    if (deltaY > deltaX && deltaY > 10) {
      event.preventDefault();
    }
  };

  const onTouchEnd = (event) => {
    if (touchStartY.current == null) return;
    const endY = event.changedTouches[0]?.clientY ?? touchStartY.current;
    const delta = touchStartY.current - endY;
    touchStartY.current = null;
    touchStartX.current = null;
    
    if (Math.abs(delta) < 50) return;
    
    if (touchLock.current) return;
    touchLock.current = true;
    
    if (delta > 0) goNext();
    else goPrev();
    
    window.setTimeout(() => {
      touchLock.current = false;
    }, 300);
  };

  const current = reels[index];
  const nextReel = reels[index + 1];

  const handleShopNow = (event) => {
    event.stopPropagation();
    if (!current?.product_id) return;
    onClose();
    
    // Check if the linked product is a combo by looking at the reel data or product data
    // For now, we'll navigate to the product page and let the backend handle combo detection
    navigate(`/products/${current.product_id}`);
  };

  const variants = {
    enter: (dir) => ({ y: dir > 0 ? '100%' : '-100%', opacity: 0.85 }),
    center: { y: 0, opacity: 1 },
    exit: (dir) => ({ y: dir > 0 ? '-100%' : '100%', opacity: 0.85 }),
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] bg-black"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
        >
          <div
            ref={containerRef}
            className="relative w-full h-full flex items-center justify-center touch-none"
            onWheel={onWheel}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            style={{ touchAction: 'none' }}
          >
            {/* Close Button - Top Right */}
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 z-50 inline-flex items-center justify-center w-12 h-12 rounded-full bg-black/55 text-white border border-[#FFCC32] hover:bg-black/70 hover:scale-110 active:scale-95 transition-all duration-200 backdrop-blur-md shadow-[0_4px 20px_rgba(255,204,50,0.3)]"
              aria-label="Close reels"
              style={{ boxShadow: '0 4px 20px rgba(255,204,50,0.3)' }}
            >
              <X size={20} strokeWidth={2.5} />
            </button>

            {reels.length === 0 ? (
              <div className="text-white/60 px-6 text-center text-sm">
                No active reels right now.
              </div>
            ) : (
              <div className="relative flex items-center justify-center w-full h-full">
                <div
                  className="
                    relative overflow-hidden bg-black
                    w-full h-full
                    md:h-[min(92vh,860px)] md:w-auto md:aspect-[9/16]
                    md:max-w-[420px] md:rounded-2xl
                    md:shadow-[0_25px_80px_rgba(0,0,0,0.65),0_0_0_1px_rgba(255,255,255,0.08)]
                  "
                >
                  <AnimatePresence initial={false} custom={direction} mode="popLayout">
                    <motion.div
                      key={current?.id || index}
                      className="absolute inset-0 bg-black"
                      custom={direction}
                      variants={variants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ type: 'spring', stiffness: 280, damping: 32 }}
                    >
                      <ReelEmbed reel={current} active preload={false} />
                    </motion.div>
                  </AnimatePresence>

                  {nextReel && (
                    <div className="absolute w-px h-px overflow-hidden opacity-0 pointer-events-none" aria-hidden>
                      <ReelEmbed reel={nextReel} active={false} preload />
                    </div>
                  )}

                  {current?.product_id && (
                    <div className="absolute bottom-8 left-0 right-0 z-30 flex justify-center px-20 pointer-events-none">
                      <button
                        type="button"
                        onClick={handleShopNow}
                        className="pointer-events-auto inline-flex items-center justify-center rounded-full px-8 py-3 bg-[#ffcc32] text-black text-sm font-bold tracking-[0.14em] shadow-[0_10px_30px_rgba(255,204,50,0.35)] hover:scale-[1.03] active:scale-[0.98] transition"
                      >
                        SHOP NOW
                      </button>
                    </div>
                  )}

                  {/* Swipe Indication - Hide after first swipe */}
                  {!hasSwiped && (
                    <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-2 animate-bounce">
                      <div className="text-white/70 text-xs font-medium tracking-wide text-center">
                        Swipe to explore
                      </div>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white/70">
                        <path d="M12 5v14M19 12l-7 7-7-7" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Navigation Buttons - Bottom Right (Stacked) */}
                <div className="absolute bottom-8 right-4 z-50 flex flex-col gap-3">
                  {/* Previous Button - Top */}
                  <button
                    type="button"
                    onClick={() => {
                      setHasSwiped(true);
                      goPrev();
                    }}
                    disabled={index === 0}
                    className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-black/55 text-white border border-[#FFCC32] hover:bg-black/70 hover:scale-110 active:scale-95 transition-all duration-200 disabled:opacity-30 disabled:hover:scale-100 backdrop-blur-md shadow-[0_4px 20px_rgba(255,204,50,0.3)]"
                    aria-label="Previous reel"
                    style={{ boxShadow: '0 4px 20px rgba(255,204,50,0.3)' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 15l-6-6-6 6" />
                    </svg>
                  </button>

                  {/* Next Button - Bottom */}
                  <button
                    type="button"
                    onClick={() => {
                      setHasSwiped(true);
                      goNext();
                    }}
                    disabled={index >= reels.length - 1}
                    className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-black/55 text-white border border-[#FFCC32] hover:bg-black/70 hover:scale-110 active:scale-95 transition-all duration-200 disabled:opacity-30 disabled:hover:scale-100 backdrop-blur-md shadow-[0_4px 20px_rgba(255,204,50,0.3)]"
                    aria-label="Next reel"
                    style={{ boxShadow: '0 4px 20px rgba(255,204,50,0.3)' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function InstagramReelsButton() {
  const [open, setOpen] = useState(false);
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadReels = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/reels');
      const raw = response?.data;
      const list = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.reels)
          ? raw.reels
          : Array.isArray(raw?.data)
            ? raw.data
            : [];
      
      // Filter active reels
      const activeReels = list.filter((reel) => reel && reel.is_active !== false);
      
      // Shuffle reels randomly for each user session
      const shuffledReels = [...activeReels].sort(() => Math.random() - 0.5);
      
      setReels(shuffledReels);
    } catch (error) {
      console.error('Failed to load Instagram reels:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReels();
  }, [loadReels]);

  const handleOpen = async () => {
    // Mark hint as seen when user clicks reels button
    sessionStorage.setItem('rride_reels_hint_seen', 'true');
    setOpen(true);
    if (!loading) {
      loadReels();
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="fixed bottom-[92px] right-6 z-50 group"
        aria-label="Open Instagram Reels"
      >
        <span
          className="relative flex items-center justify-center rounded-full text-white overflow-hidden transition-all duration-300 group-hover:scale-105 active:scale-95"
          style={{
            width: '54px',
            height: '54px',
            background: 'linear-gradient(45deg, #FEDA75, #FA7E1E, #D62976, #962FBF, #4F5BD5)',
            boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
          }}
        >
          {/* Official Instagram Reels Icon */}
          <svg 
            viewBox="0 0 24 24" 
            fill="currentColor" 
            className="w-6 h-6 text-white"
          >
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
          </svg>
        </span>
      </button>

      <InstagramReelsViewer
        open={open}
        onClose={() => setOpen(false)}
        reels={reels}
      />
    </>
  );
}
